import type { LngLat } from "../shared/types.ts";

/** Geographic bbox: [minLng, minLat, maxLng, maxLat]. */
export type GeoBBox = [number, number, number, number];

export interface SnapshotGeoFeature {
  type: "Feature";
  properties: Record<string, unknown>;
  geometry: {
    type: "LineString" | "MultiLineString";
    coordinates: LngLat[] | LngLat[][];
  };
}

export interface SnapshotFeatureCollection {
  type: "FeatureCollection";
  features: SnapshotGeoFeature[];
}

const SKIP_CLASS = new Set(["rail", "transit", "ferry", "aerialway"]);

/** Filename like `mapus-snapshot-2026-10-02.geojson`. */
export function snapshotFilename(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `mapus-snapshot-${y}-${m}-${d}.geojson`;
}

export function pointInBbox(p: LngLat, bbox: GeoBBox): boolean {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  return p[0] >= minLng && p[0] <= maxLng && p[1] >= minLat && p[1] <= maxLat;
}

/**
 * Clip a LineString against an axis-aligned geographic bbox.
 * Returns zero or more open LineStrings (line may exit and re-enter).
 */
export function clipLineStringToBbox(coords: LngLat[], bbox: GeoBBox): LngLat[][] {
  if (coords.length < 2) return [];
  const parts: LngLat[][] = [];
  let current: LngLat[] = [];

  const pushPoint = (p: LngLat): void => {
    const last = current[current.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) current.push(p);
  };

  const flush = (): void => {
    if (current.length >= 2) parts.push(current);
    current = [];
  };

  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    const clipped = clipSegmentToBbox(a, b, bbox);
    if (!clipped) {
      flush();
      continue;
    }
    const [ca, cb] = clipped;
    if (current.length === 0) {
      pushPoint(ca);
      pushPoint(cb);
    } else {
      const last = current[current.length - 1];
      if (last[0] === ca[0] && last[1] === ca[1]) {
        pushPoint(cb);
      } else {
        flush();
        pushPoint(ca);
        pushPoint(cb);
      }
    }
  }
  flush();
  return parts;
}

/** Liang–Barsky segment clip against axis-aligned bbox. */
export function clipSegmentToBbox(
  a: LngLat,
  b: LngLat,
  bbox: GeoBBox,
): [LngLat, LngLat] | null {
  const [minX, minY, maxX, maxY] = bbox;
  let t0 = 0;
  let t1 = 1;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];

  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };

  if (
    clip(-dx, a[0] - minX) &&
    clip(dx, maxX - a[0]) &&
    clip(-dy, a[1] - minY) &&
    clip(dy, maxY - a[1])
  ) {
    const ca: LngLat = t0 === 0 ? a : [a[0] + t0 * dx, a[1] + t0 * dy];
    const cb: LngLat = t1 === 1 ? b : [a[0] + t1 * dx, a[1] + t1 * dy];
    return [ca, cb];
  }
  return null;
}

type RawGeom =
  | { type: "LineString"; coordinates: LngLat[] }
  | { type: "MultiLineString"; coordinates: LngLat[][] };

export interface RawRoadFeature {
  id?: string | number;
  properties?: Record<string, unknown> | null;
  geometry: RawGeom | { type: string; coordinates: unknown };
}

/**
 * Build a FeatureCollection of road/path lines clipped to `bbox`.
 * Skips rail/transit/ferry/aerialway classes (same filter as editor roads).
 */
export function roadsToFeatureCollection(
  features: RawRoadFeature[],
  bbox: GeoBBox,
): SnapshotFeatureCollection {
  const out: SnapshotGeoFeature[] = [];
  const seen = new Set<string>();

  for (const feature of features) {
    const props = feature.properties ?? {};
    const cls = String(props.class ?? "");
    if (SKIP_CLASS.has(cls)) continue;

    const geom = feature.geometry;
    const lines: LngLat[][] = [];
    if (geom.type === "LineString" && Array.isArray(geom.coordinates)) {
      lines.push(...clipLineStringToBbox(geom.coordinates as LngLat[], bbox));
    } else if (geom.type === "MultiLineString" && Array.isArray(geom.coordinates)) {
      for (const line of geom.coordinates as LngLat[][]) {
        lines.push(...clipLineStringToBbox(line, bbox));
      }
    } else {
      continue;
    }
    if (lines.length === 0) continue;

    const key =
      feature.id != null
        ? `id:${feature.id}`
        : `g:${JSON.stringify(lines)}:${cls}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const kept: Record<string, unknown> = {};
    for (const k of ["class", "subclass", "brunnel", "layer", "oneway", "ramp", "service"]) {
      if (props[k] != null) kept[k] = props[k];
    }
    if (feature.id != null) kept.id = feature.id;

    if (lines.length === 1) {
      out.push({
        type: "Feature",
        properties: kept,
        geometry: { type: "LineString", coordinates: lines[0] },
      });
    } else {
      out.push({
        type: "Feature",
        properties: kept,
        geometry: { type: "MultiLineString", coordinates: lines },
      });
    }
  }

  return { type: "FeatureCollection", features: out };
}

export function downloadFeatureCollection(
  fc: SnapshotFeatureCollection,
  filename: string,
): void {
  const blob = new Blob([JSON.stringify(fc)], { type: "application/geo+json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
